import React from 'react';
import { useSessionSummaryQuery } from '../../store/api/localApi';

interface Props {
  sessionId: string;
}

/**
 * Register-style attendance breakdown for a session's recap: of everyone
 * expected, how many were present, left early, were planned-absent, or never
 * showed. Read-only; sits above the activity roll-up on the ended-session view.
 *
 * Renders nothing until the summary loads or if the backend didn't send a
 * breakdown (older responses). `noShow` is only meaningful once the session has
 * ended — while live the backend reports those children as `stillExpected`, and
 * we show that bucket instead so the numbers still add up to `expected`.
 */
const AttendanceBreakdown: React.FC<Props> = ({ sessionId }) => {
  const { data } = useSessionSummaryQuery(sessionId);
  const a = data?.attendance;
  if (!a) return null;

  const cells: Array<{ key: string; label: string; value: number; tone: string; testid: string }> =
    [
      {
        key: 'present',
        label: 'present',
        value: a.present,
        tone: 'text-emerald-700',
        testid: 'attendance-present',
      },
      {
        key: 'leftEarly',
        label: 'left early',
        value: a.leftEarly,
        tone: 'text-slate-600',
        testid: 'attendance-left-early',
      },
      {
        key: 'plannedAbsent',
        label: 'planned absent',
        value: a.plannedAbsent,
        tone: 'text-slate-600',
        testid: 'attendance-planned-absent',
      },
      {
        key: 'noShow',
        label: a.noShow === 1 ? 'no-show' : 'no-shows',
        value: a.noShow,
        tone: a.noShow > 0 ? 'text-amber-700' : 'text-slate-400',
        testid: 'attendance-no-show',
      },
    ];
  // Only surface the "still expected" bucket while it is non-zero (i.e. a live
  // session), so an ended recap stays clean.
  if (a.stillExpected > 0) {
    cells.push({
      key: 'stillExpected',
      label: 'still to arrive',
      value: a.stillExpected,
      tone: 'text-amber-700',
      testid: 'attendance-still-expected',
    });
  }

  return (
    <section
      data-testid="attendance-breakdown"
      className="rounded-lg border border-slate-200 bg-white p-3"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Attendance</h3>
        <span className="text-xs text-slate-500" data-testid="attendance-expected">
          {a.expected} expected
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {cells.map((c) => (
          <div
            key={c.key}
            data-testid={c.testid}
            className="rounded-md border border-slate-100 bg-slate-50 px-3 py-2"
          >
            <p className={`text-xl font-semibold tabular-nums ${c.tone}`}>{c.value}</p>
            <p className="text-[11px] text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export default AttendanceBreakdown;
